
import { ScrollArea } from "@/components/ui/scroll-area";
import PublicHeader from "@/components/layout/homepage/PublicHeader";
import { useI18n } from '@/hooks/useI18n';

const Privacy = () => {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-white">
      {/* Fixed Header */}
      <PublicHeader 
        title={t('legal.privacyTitle')} 
        subtitle={t('legal.privacyUpdated')}
      />

      {/* Content with top padding for fixed header */}
      <div className="pt-20">
        <ScrollArea className="h-[calc(100vh-80px)]">
          <main className="container mx-auto px-4 py-6 max-w-4xl">
            <div className="prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">1. Introduzione</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Place2Be rispetta la tua privacy e si impegna a proteggere i tuoi dati personali. 
                  Questa Privacy Policy spiega come raccogliamo, utilizziamo e proteggiamo le tue informazioni 
                  quando utilizzi la nostra piattaforma.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">2. Dati che Raccogliamo</h2>
                <h2 className="text-[#030A14] mb-3">Informazioni di Registrazione</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Nome e cognome</li>
                  <li>Indirizzo email</li>
                  <li>Numero di telefono</li>
                  <li>Informazioni professionali (per travel advisor)</li>
                </ul>

                <h2 className="text-[#030A14] mb-3">Dati di Utilizzo</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Attività sulla piattaforma</li>
                  <li>Preferenze di viaggio</li>
                  <li>Cronologia delle ricerche</li>
                  <li>Interazioni con travel advisor</li>
                </ul>

                <h2 className="text-[#030A14] mb-3">Dati Tecnici</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Indirizzo IP</li>
                  <li>Tipo di browser e dispositivo</li>
                  <li>Dati di geolocalizzazione (se autorizzati)</li>
                  <li>Cookie e tecnologie simili</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">3. Come Utilizziamo i Tuoi Dati</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">Utilizziamo i tuoi dati personali per:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Fornire e migliorare i nostri servizi</li>
                  <li>Facilitare la connessione tra viaggiatori e travel advisor</li>
                  <li>Elaborare pagamenti attraverso Stripe</li>
                  <li>Comunicare aggiornamenti e informazioni importanti</li>
                  <li>Personalizzare l'esperienza utente</li>
                  <li>Prevenire frodi e garantire la sicurezza</li>
                  <li>Rispettare obblighi legali</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">4. Condivisione dei Dati</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Non vendiamo i tuoi dati personali a terze parti. Condividiamo le informazioni solo quando:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>È necessario per fornire i servizi richiesti</li>
                  <li>Hai dato il tuo consenso esplicito</li>
                  <li>È richiesto dalla legge</li>
                  <li>È necessario per proteggere i diritti di Place2Be o degli utenti</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">5. Fornitori di Servizi Terzi</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">Collaboriamo con fornitori di servizi fidati, tra cui:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li><strong>Stripe:</strong> Per l'elaborazione sicura dei pagamenti</li>
                  <li><strong>Supabase:</strong> Per l'hosting del database e l'autenticazione</li>
                  <li><strong>Provider di email:</strong> Per le comunicazioni di servizio</li>
                  <li><strong>Servizi di analytics:</strong> Per migliorare l'esperienza utente</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">6. Sicurezza dei Dati</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Implementiamo misure di sicurezza tecniche e organizzative per proteggere i tuoi dati:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Crittografia dei dati in transito e a riposo</li>
                  <li>Autenticazione a due fattori</li>
                  <li>Accesso limitato ai dati su base need-to-know</li>
                  <li>Monitoraggio costante per rilevare attività sospette</li>
                  <li>Backup regolari e piani di disaster recovery</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">7. I Tuoi Diritti (GDPR)</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  In conformità al GDPR, hai i seguenti diritti sui tuoi dati personali:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li><strong>Diritto di accesso:</strong> Richiedere copia dei tuoi dati</li>
                  <li><strong>Diritto di rettifica:</strong> Correggere dati inesatti</li>
                  <li><strong>Diritto di cancellazione:</strong> Richiedere la rimozione dei dati</li>
                  <li><strong>Diritto di limitazione:</strong> Limitare il trattamento</li>
                  <li><strong>Diritto di portabilità:</strong> Ricevere i dati in formato strutturato</li>
                  <li><strong>Diritto di opposizione:</strong> Opporsi al trattamento</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">8. Cookie e Tecnologie di Tracciamento</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Utilizziamo cookie e tecnologie simili per:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Mantenere la sessione di login</li>
                  <li>Ricordare le tue preferenze</li>
                  <li>Analizzare l'utilizzo della piattaforma</li>
                  <li>Personalizzare i contenuti</li>
                </ul>
                <p className="text-gray-700 mt-4 leading-relaxed">
                  Puoi gestire le impostazioni dei cookie attraverso il tuo browser.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">9. Conservazione dei Dati</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Conserviamo i tuoi dati personali solo per il tempo necessario a:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Fornire i servizi richiesti</li>
                  <li>Rispettare obblighi legali</li>
                  <li>Risolvere controversie</li>
                  <li>Far valere i nostri diritti legali</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">10. Modifiche alla Privacy Policy</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Possiamo aggiornare questa Privacy Policy periodicamente. Ti notificheremo 
                  eventuali modifiche significative tramite email o attraverso la piattaforma.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-[#030A14] mb-4">11. Contatti</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Per domande sulla privacy o per esercitare i tuoi diritti, contattaci a:
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Email: privacy@place2be.com<br />
                  Telefono: +39 012 345 6789<br />
                  Indirizzo: Via Milano 123, 20100 Milano, Italia<br />
                  Data Protection Officer: dpo@place2be.com
                </p>
              </section>
            </div>
          </main>
        </ScrollArea>
      </div>
    </div>
  );
};

export default Privacy;
